import { api, ApiError, type Note, type TreeResponse } from "../api/client";
import { saveLegacyNoteAuthorityFirst } from "./legacyNoteSave";

const LEGACY_DB_NAME = "notey-g-app";
const DB_VERSION = 2;
const TREE_KEY = "latest";

export type OfflineStorageScope = {
  serverUrl: string;
  accountId: string;
};

export type OfflineOutboxItem = {
  id?: number;
  noteId: string;
  title: string;
  body: string;
  tags?: string[];
  folderId?: string;
  updatedAt: string;
};

export type OfflineSaveConflict = Omit<OfflineOutboxItem, "id"> & {
  id: string;
  sourceOutboxId?: number;
  failedAt: string;
  status: number;
  message: string;
};

export type OfflineSaveFlushResult = {
  flushed: number;
  pending: number;
  conflicts: number;
  failed: number;
  lastError?: string;
};

export type PendingRichNoteCreate = {
  id: "blank-rich-canary";
  idempotencyKey: string;
  title: string;
  body: "";
  folderId: string;
  createdAt: string;
  allowLegacyFallback: boolean;
  /** Older intents may have used the untracked Markdown fallback once. */
  legacyFallbackPossible: boolean;
};

export function normalizePendingRichNoteCreate(
  intent: PendingRichNoteCreate
): PendingRichNoteCreate {
  return {
    ...intent,
    allowLegacyFallback: false,
    // Records written before exact guarded/deterministic recovery do not have
    // this marker and may have used the old untracked Markdown fallback.
    legacyFallbackPossible: intent.legacyFallbackPossible !== false
  };
}

export type OrdinaryNoteCreateInput = {
  title: string;
  body: string;
  folderId?: string;
  tags?: string[];
  options?: {
    aiManaged?: boolean;
    pinned?: boolean;
  };
};

export type PendingOrdinaryNoteCreate = {
  id: "ordinary-web-create";
  idempotencyKey: string;
  title: string;
  body: string;
  folderId: string;
  tags: string[];
  options: {
    aiManaged?: boolean;
    pinned?: boolean;
  };
  createdAt: string;
};

export type PendingOrdinaryNoteCreateAttempt = {
  intent: PendingOrdinaryNoteCreate;
  reused: boolean;
  matchesInput: boolean;
};

export type PendingFolderCreate = {
  id: "folder-web-create";
  parentId: string;
  name: string;
  idempotencyKey: string;
  createdAt: string;
};

export type PendingFolderCreateAttempt = {
  intent: PendingFolderCreate;
  reused: boolean;
  matchesInput: boolean;
};

export type PendingFolderMutation = {
  id: string;
  operation: "folder.update" | "folder.move" | "folder.trash";
  folderId: string;
  fromRev: number;
  sourceParentId: string;
  name?: string;
  parentId?: string;
  idempotencyKey: string;
  createdAt: string;
  quarantinedAt?: string;
};

export type PendingFolderMutationAttempt = {
  intent: PendingFolderMutation;
  reused: boolean;
  matchesInput: boolean;
};

export type PendingNoteMove = {
  id: string;
  noteId: string;
  fromRev: number;
  targetFolderId: string;
  idempotencyKey: string;
  createdAt: string;
};

export type PendingNoteMoveAttempt = {
  intent: PendingNoteMove;
  reused: boolean;
  matchesTarget: boolean;
};

export type PendingNoteTrash = {
  id: string;
  noteId: string;
  fromRev: number;
  idempotencyKey: string;
  createdAt: string;
};

export type PendingNoteTrashAttempt = {
  intent: PendingNoteTrash;
  reused: boolean;
};

export type PendingNoteRestore = {
  id: string;
  noteId: string;
  fromRev: number;
  idempotencyKey: string;
  createdAt: string;
};

export type PendingNoteRestoreAttempt = {
  intent: PendingNoteRestore;
  reused: boolean;
};

export type PendingFolderRestore = {
  id: string;
  folderId: string;
  fromRev: number;
  originalParentId: string;
  idempotencyKey: string;
  createdAt: string;
  quarantinedAt?: string;
};

export type PendingFolderRestoreAttempt = {
  intent: PendingFolderRestore;
  reused: boolean;
};

export class OrdinaryNoteCreateIntentError extends Error {
  intent: PendingOrdinaryNoteCreate;
  cause: unknown;

  constructor(message: string, intent: PendingOrdinaryNoteCreate, cause?: unknown) {
    super(message);
    this.name = "OrdinaryNoteCreateIntentError";
    this.intent = intent;
    this.cause = cause;
  }
}

export class NoteMoveIntentError extends Error {
  intent: PendingNoteMove;
  cause: unknown;

  constructor(message: string, intent: PendingNoteMove, cause?: unknown) {
    super(message);
    this.name = "NoteMoveIntentError";
    this.intent = intent;
    this.cause = cause;
  }
}

export class NoteTrashIntentError extends Error {
  intent: PendingNoteTrash;
  cause: unknown;

  constructor(message: string, intent: PendingNoteTrash, cause?: unknown) {
    super(message);
    this.name = "NoteTrashIntentError";
    this.intent = intent;
    this.cause = cause;
  }
}

export class FolderCreateIntentError extends Error {
  intent: PendingFolderCreate;
  cause: unknown;

  constructor(message: string, intent: PendingFolderCreate, cause?: unknown) {
    super(message);
    this.name = "FolderCreateIntentError";
    this.intent = intent;
    this.cause = cause;
  }
}

export class NoteRestoreIntentError extends Error {
  intent: PendingNoteRestore;
  cause: unknown;

  constructor(message: string, intent: PendingNoteRestore, cause?: unknown) {
    super(message);
    this.name = "NoteRestoreIntentError";
    this.intent = intent;
    this.cause = cause;
  }
}

export class FolderMutationIntentError extends Error {
  intent: PendingFolderMutation;
  cause: unknown;

  constructor(message: string, intent: PendingFolderMutation, cause?: unknown) {
    super(message);
    this.name = "FolderMutationIntentError";
    this.intent = intent;
    this.cause = cause;
  }
}

export class FolderRestoreIntentError extends Error {
  intent: PendingFolderRestore;
  cause: unknown;

  constructor(message: string, intent: PendingFolderRestore, cause?: unknown) {
    super(message);
    this.name = "FolderRestoreIntentError";
    this.intent = intent;
    this.cause = cause;
  }
}

export function ordinaryNoteCreateIntentDisposition(
  error: unknown
): "definitive-no-commit" | "preserve" {
  const cause = error instanceof OrdinaryNoteCreateIntentError
    ? error.cause
    : error;
  if (!(cause instanceof ApiError)) return "preserve";
  if (
    cause.status >= 400
    && cause.status < 500
    && ![408, 409, 425, 429].includes(cause.status)
  ) {
    return "definitive-no-commit";
  }
  return "preserve";
}

export function noteMoveIntentDisposition(
  error: unknown
): "definitive-no-commit" | "preserve" {
  const cause = error instanceof NoteMoveIntentError ? error.cause : error;
  if (!(cause instanceof ApiError)) return "preserve";
  if (
    cause.status >= 400
    && cause.status < 500
    && ![408, 409, 425, 429].includes(cause.status)
  ) {
    return "definitive-no-commit";
  }
  return "preserve";
}

export function noteTrashIntentDisposition(
  error: unknown
): "definitive-no-commit" | "preserve" {
  const cause = error instanceof NoteTrashIntentError ? error.cause : error;
  if (!(cause instanceof ApiError)) return "preserve";
  if (
    cause.status >= 400
    && cause.status < 500
    && ![408, 409, 425, 429].includes(cause.status)
  ) {
    return "definitive-no-commit";
  }
  return "preserve";
}

function guardedIntentDisposition(error: unknown): "definitive-no-commit" | "preserve" {
  if (!(error instanceof ApiError)) return "preserve";
  if (
    error.status >= 400
    && error.status < 500
    && ![408, 409, 425, 429].includes(error.status)
  ) {
    return "definitive-no-commit";
  }
  return "preserve";
}

export function folderCreateIntentDisposition(
  error: unknown
): "definitive-no-commit" | "preserve" {
  return guardedIntentDisposition(
    error instanceof FolderCreateIntentError ? error.cause : error
  );
}

export function noteRestoreIntentDisposition(
  error: unknown
): "definitive-no-commit" | "preserve" {
  return guardedIntentDisposition(
    error instanceof NoteRestoreIntentError ? error.cause : error
  );
}

export function folderMutationIntentDisposition(
  error: unknown
): "definitive-no-commit" | "quarantine" | "preserve" {
  const intent = error instanceof FolderMutationIntentError
    ? error.intent
    : null;
  const cause = error instanceof FolderMutationIntentError
    ? error.cause
    : error;
  if (intent?.quarantinedAt) return "quarantine";
  if (cause instanceof ApiError && cause.status === 409) return "quarantine";
  return guardedIntentDisposition(cause);
}

export function folderRestoreIntentDisposition(
  error: unknown
): "definitive-no-commit" | "quarantine" | "preserve" {
  const intent = error instanceof FolderRestoreIntentError
    ? error.intent
    : null;
  const cause = error instanceof FolderRestoreIntentError
    ? error.cause
    : error;
  if (intent?.quarantinedAt) return "quarantine";
  if (cause instanceof ApiError && cause.status === 409) return "quarantine";
  return guardedIntentDisposition(cause);
}

function canUseIndexedDb() {
  return typeof indexedDB !== "undefined";
}

function normalizeServerUrl(value: string) {
  try {
    return new URL(value).origin.toLowerCase();
  } catch {
    return "";
  }
}

export function createOfflineStorageScope(
  accountId: string,
  serverUrl = typeof window !== "undefined" ? window.location.origin : ""
): OfflineStorageScope | null {
  const normalizedAccountId = String(accountId || "").trim();
  const normalizedServerUrl = normalizeServerUrl(serverUrl);
  if (!normalizedAccountId || !normalizedServerUrl) return null;
  return {
    serverUrl: normalizedServerUrl,
    accountId: normalizedAccountId
  };
}

function requireScope(scope: OfflineStorageScope) {
  const normalized = createOfflineStorageScope(scope?.accountId, scope?.serverUrl);
  if (!normalized) throw new Error("Offline storage requires an authenticated account scope.");
  return normalized;
}

export function offlineStorageDatabaseName(scope: OfflineStorageScope) {
  const normalized = requireScope(scope);
  return [
    "notey-g-app-scoped-v1",
    encodeURIComponent(normalized.serverUrl),
    encodeURIComponent(normalized.accountId)
  ].join("::");
}

function openOfflineDb(scope: OfflineStorageScope): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!canUseIndexedDb()) {
      reject(new Error("Offline storage is not available in this browser."));
      return;
    }
    const request = indexedDB.open(offlineStorageDatabaseName(scope), DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta");
      if (!db.objectStoreNames.contains("notes")) db.createObjectStore("notes", { keyPath: "id" });
      if (!db.objectStoreNames.contains("outbox")) {
        db.createObjectStore("outbox", { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains("outboxConflicts")) {
        db.createObjectStore("outboxConflicts", {
          keyPath: "id"
        });
      }
      if (!db.objectStoreNames.contains("pendingCreates")) {
        db.createObjectStore("pendingCreates", { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Could not open offline storage."));
  });
}

async function withStore<T>(
  scope: OfflineStorageScope,
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | undefined> {
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result: T | undefined;
    tx.oncomplete = () => {
      db.close();
      resolve(result);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Offline storage transaction failed."));
    };
    const request = run(store);
    if (request) {
      request.onsuccess = () => {
        result = request.result;
      };
    }
  });
}

export async function cacheTree(scope: OfflineStorageScope, tree: TreeResponse) {
  await withStore(scope, "meta", "readwrite", (store) => store.put({ tree, cachedAt: new Date().toISOString() }, TREE_KEY));
}

export async function readCachedTree(scope: OfflineStorageScope): Promise<TreeResponse | null> {
  const row = await withStore<{ tree?: TreeResponse }>(scope, "meta", "readonly", (store) => store.get(TREE_KEY));
  return row?.tree || null;
}

export async function cacheNote(scope: OfflineStorageScope, note: Note) {
  await withStore(scope, "notes", "readwrite", (store) => store.put({ ...note, cachedAt: new Date().toISOString() }));
}

export async function readCachedNote(scope: OfflineStorageScope, id: string): Promise<Note | null> {
  const row = await withStore<Note>(scope, "notes", "readonly", (store) => store.get(id));
  return row || null;
}

export async function beginPendingRichNoteCreateAttempt(
  scope: OfflineStorageScope,
  input: { title: string; folderId?: string }
): Promise<PendingRichNoteCreate> {
  const id = "blank-rich-canary" as const;
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingRichNoteCreate | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Rich note creation intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error("Rich note creation intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingRichNoteCreate | undefined;
      captured = existing
        ? normalizePendingRichNoteCreate(existing)
        : {
            id,
            idempotencyKey: crypto.randomUUID(),
            title: String(input.title || "New note"),
            body: "",
            folderId: String(input.folderId || ""),
            createdAt: new Date().toISOString(),
            allowLegacyFallback: true,
            legacyFallbackPossible: false
          };
      store.put({ ...captured, allowLegacyFallback: false });
    };
  });
}

export async function clearPendingRichNoteCreate(
  scope: OfflineStorageScope,
  idempotencyKey: string
): Promise<boolean> {
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Rich note creation intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get("blank-rich-canary");
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingRichNoteCreate | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete("blank-rich-canary");
      }
    };
  });
}

export function readPendingRichNoteCreate(
  scope: OfflineStorageScope
): Promise<PendingRichNoteCreate | null> {
  return withStore<PendingRichNoteCreate>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.get("blank-rich-canary")
  ).then(intent => intent ? normalizePendingRichNoteCreate(intent) : null);
}

function canonicalOrdinaryNoteCreate(
  input: OrdinaryNoteCreateInput
): Omit<PendingOrdinaryNoteCreate, "id" | "idempotencyKey" | "createdAt"> {
  const options = input.options || {};
  return {
    title: String(input.title || "Untitled"),
    body: String(input.body || ""),
    folderId: String(input.folderId || ""),
    tags: Array.isArray(input.tags) ? input.tags.map(tag => String(tag)) : [],
    options: {
      ...(options.aiManaged !== undefined ? { aiManaged: Boolean(options.aiManaged) } : {}),
      ...(options.pinned !== undefined ? { pinned: Boolean(options.pinned) } : {})
    }
  };
}

function sameOrdinaryNoteCreate(
  intent: PendingOrdinaryNoteCreate,
  input: OrdinaryNoteCreateInput
) {
  const canonical = canonicalOrdinaryNoteCreate(input);
  return JSON.stringify({
    title: intent.title,
    body: intent.body,
    folderId: intent.folderId,
    tags: intent.tags,
    options: intent.options
  }) === JSON.stringify(canonical);
}

export async function beginPendingOrdinaryNoteCreateAttempt(
  scope: OfflineStorageScope,
  input: OrdinaryNoteCreateInput
): Promise<PendingOrdinaryNoteCreateAttempt> {
  const id = "ordinary-web-create" as const;
  const canonical = canonicalOrdinaryNoteCreate(input);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingOrdinaryNoteCreateAttempt | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note creation intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error("Note creation intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingOrdinaryNoteCreate | undefined;
      if (existing) {
        captured = {
          intent: existing,
          reused: true,
          matchesInput: sameOrdinaryNoteCreate(existing, canonical)
        };
        return;
      }
      const intent: PendingOrdinaryNoteCreate = {
        id,
        idempotencyKey: `webcreate:${crypto.randomUUID()}`,
        ...canonical,
        createdAt: new Date().toISOString()
      };
      captured = { intent, reused: false, matchesInput: true };
      store.put(intent);
    };
  });
}

export function readPendingOrdinaryNoteCreate(
  scope: OfflineStorageScope
): Promise<PendingOrdinaryNoteCreate | null> {
  return withStore<PendingOrdinaryNoteCreate>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.get("ordinary-web-create")
  ).then(intent => intent || null);
}

export async function clearPendingOrdinaryNoteCreate(
  scope: OfflineStorageScope,
  idempotencyKey: string
): Promise<boolean> {
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note creation intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get("ordinary-web-create");
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingOrdinaryNoteCreate | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete("ordinary-web-create");
      }
    };
  });
}

export async function createOrdinaryNoteWithIntent(
  scope: OfflineStorageScope,
  input: OrdinaryNoteCreateInput
) {
  const attempt = await beginPendingOrdinaryNoteCreateAttempt(scope, input);
  if (!attempt.matchesInput) {
    throw new OrdinaryNoteCreateIntentError(
      "An earlier note is still being recovered. Refresh Notes and retry that note before creating another.",
      attempt.intent
    );
  }
  const { intent } = attempt;
  try {
    const response = await api.createNote(
      intent.title,
      intent.body,
      intent.folderId,
      {
        tags: intent.tags,
        ...intent.options,
        idempotencyKey: intent.idempotencyKey
      }
    );
    return { response, intent, reused: attempt.reused };
  } catch (error) {
    throw new OrdinaryNoteCreateIntentError(
      error instanceof Error ? error.message : "Could not create note.",
      intent,
      error
    );
  }
}

function sameFolderCreate(
  intent: PendingFolderCreate,
  input: { parentId?: string; name: string }
) {
  return intent.parentId === String(input.parentId || "")
    && intent.name === String(input.name || "").trim();
}

export async function beginPendingFolderCreateAttempt(
  scope: OfflineStorageScope,
  input: { parentId?: string; name: string }
): Promise<PendingFolderCreateAttempt> {
  const parentId = String(input.parentId || "");
  const name = String(input.name || "").trim();
  if (!name) throw new Error("A folder name is required.");
  const id = "folder-web-create" as const;
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingFolderCreateAttempt | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder creation intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error("Folder creation intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingFolderCreate | undefined;
      if (existing) {
        captured = {
          intent: existing,
          reused: true,
          matchesInput: sameFolderCreate(existing, { parentId, name })
        };
        return;
      }
      const intent: PendingFolderCreate = {
        id,
        parentId,
        name,
        idempotencyKey: `webfoldercreate:${crypto.randomUUID()}`,
        createdAt: new Date().toISOString()
      };
      captured = { intent, reused: false, matchesInput: true };
      store.put(intent);
    };
  });
}

export function readPendingFolderCreate(
  scope: OfflineStorageScope
): Promise<PendingFolderCreate | null> {
  return withStore<PendingFolderCreate>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.get("folder-web-create")
  ).then(intent => intent || null);
}

export async function clearPendingFolderCreate(
  scope: OfflineStorageScope,
  idempotencyKey: string
): Promise<boolean> {
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder creation intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get("folder-web-create");
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingFolderCreate | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete("folder-web-create");
      }
    };
  });
}

export async function createFolderWithIntent(
  scope: OfflineStorageScope,
  input: { parentId?: string; name: string }
) {
  const attempt = await beginPendingFolderCreateAttempt(scope, input);
  if (!attempt.matchesInput) {
    throw new FolderCreateIntentError(
      "An earlier folder is still being recovered. Retry that folder before creating another.",
      attempt.intent
    );
  }
  const { intent } = attempt;
  try {
    const response = await api.createFolder(intent.parentId, intent.name, {
      idempotencyKey: intent.idempotencyKey
    });
    return { response, intent, reused: attempt.reused };
  } catch (error) {
    throw new FolderCreateIntentError(
      error instanceof Error ? error.message : "Could not create folder.",
      intent,
      error
    );
  }
}

function folderMutationIntentId(folderId: string) {
  return `folder-mutation:${String(folderId || "").trim()}`;
}

function canonicalFolderMutation(input: {
  operation: PendingFolderMutation["operation"];
  folderId: string;
  fromRev: number;
  sourceParentId?: string;
  name?: string;
  parentId?: string;
}) {
  const operation = input.operation;
  const folderId = String(input.folderId || "").trim();
  const fromRev = Number(input.fromRev);
  const sourceParentId = String(input.sourceParentId || "");
  if (!folderId || !Number.isSafeInteger(fromRev) || fromRev < 1) {
    throw new Error("Changing a folder requires its exact current revision.");
  }
  if (operation === "folder.update") {
    const name = String(input.name || "").trim();
    if (!name) throw new Error("A folder name is required.");
    return { operation, folderId, fromRev, sourceParentId, name } as const;
  }
  if (operation === "folder.move") {
    return {
      operation,
      folderId,
      fromRev,
      sourceParentId,
      parentId: String(input.parentId || "")
    } as const;
  }
  return { operation, folderId, fromRev, sourceParentId } as const;
}

function sameFolderMutation(
  intent: PendingFolderMutation,
  input: ReturnType<typeof canonicalFolderMutation>
) {
  return intent.operation === input.operation
    && intent.folderId === input.folderId
    && intent.sourceParentId === input.sourceParentId
    && (intent.name || "") === ("name" in input ? input.name : "")
    && (intent.parentId || "") === ("parentId" in input ? input.parentId : "");
}

function folderMutationKeyPrefix(operation: PendingFolderMutation["operation"]) {
  if (operation === "folder.update") return "webfolderrename";
  if (operation === "folder.move") return "webfoldermove";
  return "webfoldertrash";
}

export async function beginPendingFolderMutationAttempt(
  scope: OfflineStorageScope,
  input: {
    operation: PendingFolderMutation["operation"];
    folderId: string;
    fromRev: number;
    sourceParentId?: string;
    name?: string;
    parentId?: string;
  }
): Promise<PendingFolderMutationAttempt> {
  const canonical = canonicalFolderMutation(input);
  const id = folderMutationIntentId(canonical.folderId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingFolderMutationAttempt | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder mutation intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error("Folder mutation intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingFolderMutation | undefined;
      if (existing) {
        captured = {
          intent: existing,
          reused: true,
          matchesInput: sameFolderMutation(existing, canonical)
        };
        return;
      }
      const intent: PendingFolderMutation = {
        id,
        ...canonical,
        idempotencyKey: `${folderMutationKeyPrefix(canonical.operation)}:${crypto.randomUUID()}`,
        createdAt: new Date().toISOString()
      };
      captured = { intent, reused: false, matchesInput: true };
      store.put(intent);
    };
  });
}

export function readPendingFolderMutations(
  scope: OfflineStorageScope
): Promise<PendingFolderMutation[]> {
  return withStore<PendingFolderMutation[]>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.getAll()
  ).then(items => (items || []).filter(item => (
    typeof item?.id === "string" && item.id.startsWith("folder-mutation:")
  )));
}

export async function clearPendingFolderMutation(
  scope: OfflineStorageScope,
  folderId: string,
  idempotencyKey: string
): Promise<boolean> {
  const id = folderMutationIntentId(folderId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder mutation intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingFolderMutation | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete(id);
      }
    };
  });
}

export async function quarantinePendingFolderMutation(
  scope: OfflineStorageScope,
  intent: PendingFolderMutation
): Promise<PendingFolderMutation | null> {
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let saved: PendingFolderMutation | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder conflict could not be quarantined."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(saved);
    };
    const read = store.get(intent.id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const current = read.result as PendingFolderMutation | undefined;
      if (current?.idempotencyKey !== intent.idempotencyKey) return;
      saved = {
        ...current,
        quarantinedAt: current.quarantinedAt || new Date().toISOString()
      };
      store.put(saved);
    };
  });
}

export async function mutateFolderWithIntent(
  scope: OfflineStorageScope,
  input: {
    operation: PendingFolderMutation["operation"];
    folderId: string;
    fromRev: number;
    sourceParentId?: string;
    name?: string;
    parentId?: string;
  }
) {
  const attempt = await beginPendingFolderMutationAttempt(scope, input);
  const { intent } = attempt;
  if (!attempt.matchesInput) {
    throw new FolderMutationIntentError(
      "This folder has an earlier change awaiting recovery. Resolve that exact change before trying another.",
      intent
    );
  }
  if (intent.quarantinedAt) {
    throw new FolderMutationIntentError(
      "This folder change was quarantined after a revision conflict. Refresh before deciding whether to retry it.",
      intent
    );
  }
  try {
    const response = intent.operation === "folder.update"
      ? await api.updateFolder(intent.folderId, { name: intent.name || "" }, {
          baseRev: intent.fromRev,
          idempotencyKey: intent.idempotencyKey
        })
      : intent.operation === "folder.move"
        ? await api.updateFolder(intent.folderId, { parentId: intent.parentId || "" }, {
            baseRev: intent.fromRev,
            idempotencyKey: intent.idempotencyKey
          })
        : await api.deleteFolder(intent.folderId, {
            baseRev: intent.fromRev,
            idempotencyKey: intent.idempotencyKey
          });
    return { response, intent, reused: attempt.reused };
  } catch (error) {
    throw new FolderMutationIntentError(
      error instanceof Error ? error.message : "Could not change folder.",
      intent,
      error
    );
  }
}

function noteMoveIntentId(noteId: string) {
  return `note-move:${String(noteId || "").trim()}`;
}

export async function beginPendingNoteMoveAttempt(
  scope: OfflineStorageScope,
  input: { noteId: string; fromRev: number; targetFolderId?: string }
): Promise<PendingNoteMoveAttempt> {
  const noteId = String(input.noteId || "").trim();
  const fromRev = Number(input.fromRev);
  const targetFolderId = String(input.targetFolderId || "");
  if (!noteId || !Number.isSafeInteger(fromRev) || fromRev < 1) {
    throw new Error("Moving a note requires its exact current revision.");
  }
  const id = noteMoveIntentId(noteId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingNoteMoveAttempt | null = null;
    let conflictError = "";
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note move intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error(conflictError || "Note move intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingNoteMove | undefined;
      if (existing) {
        captured = {
          intent: existing,
          reused: true,
          matchesTarget: existing.noteId === noteId
            && existing.targetFolderId === targetFolderId
        };
        return;
      }
      const conflictingTrash = store.get(noteTrashIntentId(noteId));
      conflictingTrash.onerror = () => tx.abort();
      conflictingTrash.onsuccess = () => {
        if (conflictingTrash.result) {
          conflictError = "This note has an earlier deletion awaiting recovery.";
          return;
        }
        const intent: PendingNoteMove = {
          id,
          noteId,
          fromRev,
          targetFolderId,
          idempotencyKey: `webmove:${crypto.randomUUID()}`,
          createdAt: new Date().toISOString()
        };
        captured = { intent, reused: false, matchesTarget: true };
        store.put(intent);
      };
    };
  });
}

export function readPendingNoteMove(
  scope: OfflineStorageScope,
  noteId: string
): Promise<PendingNoteMove | null> {
  return withStore<PendingNoteMove>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.get(noteMoveIntentId(noteId))
  ).then(intent => intent || null);
}

export async function clearPendingNoteMove(
  scope: OfflineStorageScope,
  noteId: string,
  idempotencyKey: string
): Promise<boolean> {
  const id = noteMoveIntentId(noteId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note move intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingNoteMove | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete(id);
      }
    };
  });
}

export async function moveNoteWithIntent(
  scope: OfflineStorageScope,
  input: { noteId: string; fromRev: number; targetFolderId?: string }
) {
  const attempt = await beginPendingNoteMoveAttempt(scope, input);
  if (!attempt.matchesTarget) {
    throw new NoteMoveIntentError(
      "An earlier move for this note is still being recovered. Retry that destination before moving it elsewhere.",
      attempt.intent
    );
  }
  const { intent } = attempt;
  try {
    const response = await api.moveNote(
      intent.noteId,
      intent.fromRev,
      intent.targetFolderId,
      intent.idempotencyKey
    );
    return { response, intent, reused: attempt.reused };
  } catch (error) {
    throw new NoteMoveIntentError(
      error instanceof Error ? error.message : "Could not move note.",
      intent,
      error
    );
  }
}

function noteTrashIntentId(noteId: string) {
  return `note-trash:${String(noteId || "").trim()}`;
}

export async function beginPendingNoteTrashAttempt(
  scope: OfflineStorageScope,
  input: { noteId: string; fromRev: number }
): Promise<PendingNoteTrashAttempt> {
  const noteId = String(input.noteId || "").trim();
  const fromRev = Number(input.fromRev);
  if (!noteId || !Number.isSafeInteger(fromRev) || fromRev < 1) {
    throw new Error("Deleting a note requires its exact current revision.");
  }
  const id = noteTrashIntentId(noteId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingNoteTrashAttempt | null = null;
    let conflictError = "";
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note deletion intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error(conflictError || "Note deletion intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingNoteTrash | undefined;
      if (existing) {
        captured = { intent: existing, reused: true };
        return;
      }
      const conflictingMove = store.get(noteMoveIntentId(noteId));
      conflictingMove.onerror = () => tx.abort();
      conflictingMove.onsuccess = () => {
        if (conflictingMove.result) {
          conflictError = "This note has an earlier move awaiting recovery.";
          return;
        }
        const intent: PendingNoteTrash = {
          id,
          noteId,
          fromRev,
          idempotencyKey: `webtrash:${crypto.randomUUID()}`,
          createdAt: new Date().toISOString()
        };
        captured = { intent, reused: false };
        store.put(intent);
      };
    };
  });
}

export function readPendingNoteTrash(
  scope: OfflineStorageScope,
  noteId: string
): Promise<PendingNoteTrash | null> {
  return withStore<PendingNoteTrash>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.get(noteTrashIntentId(noteId))
  ).then(intent => intent || null);
}

export async function clearPendingNoteTrash(
  scope: OfflineStorageScope,
  noteId: string,
  idempotencyKey: string
): Promise<boolean> {
  const id = noteTrashIntentId(noteId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note deletion intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingNoteTrash | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete(id);
      }
    };
  });
}

export async function trashNoteWithIntent(
  scope: OfflineStorageScope,
  input: { noteId: string; fromRev: number }
) {
  const attempt = await beginPendingNoteTrashAttempt(scope, input);
  const { intent } = attempt;
  try {
    const response = await api.deleteNote(intent.noteId, {
      baseRev: intent.fromRev,
      idempotencyKey: intent.idempotencyKey
    });
    return { response, intent, reused: attempt.reused };
  } catch (error) {
    throw new NoteTrashIntentError(
      error instanceof Error ? error.message : "Could not delete note.",
      intent,
      error
    );
  }
}

function noteRestoreIntentId(noteId: string) {
  return `note-restore:${String(noteId || "").trim()}`;
}

export async function beginPendingNoteRestoreAttempt(
  scope: OfflineStorageScope,
  input: { noteId: string; fromRev: number }
): Promise<PendingNoteRestoreAttempt> {
  const noteId = String(input.noteId || "").trim();
  const fromRev = Number(input.fromRev);
  if (!noteId || !Number.isSafeInteger(fromRev) || fromRev < 1) {
    throw new Error("Restoring a note requires its exact trashed revision.");
  }
  const id = noteRestoreIntentId(noteId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingNoteRestoreAttempt | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note restore intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error("Note restore intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingNoteRestore | undefined;
      if (existing) {
        captured = { intent: existing, reused: true };
        return;
      }
      const intent: PendingNoteRestore = {
        id,
        noteId,
        fromRev,
        idempotencyKey: `webrestore:${crypto.randomUUID()}`,
        createdAt: new Date().toISOString()
      };
      captured = { intent, reused: false };
      store.put(intent);
    };
  });
}

export function readPendingNoteRestore(
  scope: OfflineStorageScope,
  noteId: string
): Promise<PendingNoteRestore | null> {
  return withStore<PendingNoteRestore>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.get(noteRestoreIntentId(noteId))
  ).then(intent => intent || null);
}

export function readPendingNoteRestores(
  scope: OfflineStorageScope
): Promise<PendingNoteRestore[]> {
  return withStore<PendingNoteRestore[]>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.getAll()
  ).then(items => (items || []).filter(item => (
    typeof item?.id === "string" && item.id.startsWith("note-restore:")
  )));
}

export async function clearPendingNoteRestore(
  scope: OfflineStorageScope,
  noteId: string,
  idempotencyKey: string
): Promise<boolean> {
  const id = noteRestoreIntentId(noteId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Note restore intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingNoteRestore | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete(id);
      }
    };
  });
}

export async function restoreNoteWithIntent(
  scope: OfflineStorageScope,
  input: { noteId: string; fromRev: number }
) {
  const attempt = await beginPendingNoteRestoreAttempt(scope, input);
  const { intent } = attempt;
  try {
    const response = await api.restoreTrash("note", intent.noteId, {
      baseRev: intent.fromRev,
      idempotencyKey: intent.idempotencyKey
    });
    return { response, intent, reused: attempt.reused };
  } catch (error) {
    throw new NoteRestoreIntentError(
      error instanceof Error ? error.message : "Could not restore note.",
      intent,
      error
    );
  }
}

function folderRestoreIntentId(folderId: string) {
  return `folder-restore:${String(folderId || "").trim()}`;
}

export async function beginPendingFolderRestoreAttempt(
  scope: OfflineStorageScope,
  input: { folderId: string; fromRev: number; originalParentId?: string }
): Promise<PendingFolderRestoreAttempt> {
  const folderId = String(input.folderId || "").trim();
  const fromRev = Number(input.fromRev);
  const originalParentId = String(input.originalParentId || "");
  if (!folderId || !Number.isSafeInteger(fromRev) || fromRev < 1) {
    throw new Error("Restoring a folder requires its exact trashed revision.");
  }
  const id = folderRestoreIntentId(folderId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let captured: PendingFolderRestoreAttempt | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder restore intent could not be stored."));
    };
    tx.oncomplete = () => {
      db.close();
      if (captured) resolve(captured);
      else reject(new Error("Folder restore intent could not be stored."));
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingFolderRestore | undefined;
      if (existing) {
        captured = { intent: existing, reused: true };
        return;
      }
      const intent: PendingFolderRestore = {
        id,
        folderId,
        fromRev,
        originalParentId,
        idempotencyKey: `webfolderrestore:${crypto.randomUUID()}`,
        createdAt: new Date().toISOString()
      };
      captured = { intent, reused: false };
      store.put(intent);
    };
  });
}

export function readPendingFolderRestores(
  scope: OfflineStorageScope
): Promise<PendingFolderRestore[]> {
  return withStore<PendingFolderRestore[]>(
    scope,
    "pendingCreates",
    "readonly",
    store => store.getAll()
  ).then(items => (items || []).filter(item => (
    typeof item?.id === "string" && item.id.startsWith("folder-restore:")
  )));
}

export async function clearPendingFolderRestore(
  scope: OfflineStorageScope,
  folderId: string,
  idempotencyKey: string
): Promise<boolean> {
  const id = folderRestoreIntentId(folderId);
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let cleared = false;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder restore intent could not be cleared."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(cleared);
    };
    const read = store.get(id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const existing = read.result as PendingFolderRestore | undefined;
      if (existing?.idempotencyKey === idempotencyKey) {
        cleared = true;
        store.delete(id);
      }
    };
  });
}

export async function quarantinePendingFolderRestore(
  scope: OfflineStorageScope,
  intent: PendingFolderRestore
): Promise<PendingFolderRestore | null> {
  const db = await openOfflineDb(scope);
  return new Promise((resolve, reject) => {
    const tx = db.transaction("pendingCreates", "readwrite");
    const store = tx.objectStore("pendingCreates");
    let saved: PendingFolderRestore | null = null;
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Folder restore conflict could not be quarantined."));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(saved);
    };
    const read = store.get(intent.id);
    read.onerror = () => tx.abort();
    read.onsuccess = () => {
      const current = read.result as PendingFolderRestore | undefined;
      if (current?.idempotencyKey !== intent.idempotencyKey) return;
      saved = {
        ...current,
        quarantinedAt: current.quarantinedAt || new Date().toISOString()
      };
      store.put(saved);
    };
  });
}

export async function restoreFolderWithIntent(
  scope: OfflineStorageScope,
  input: { folderId: string; fromRev: number; originalParentId?: string }
) {
  const attempt = await beginPendingFolderRestoreAttempt(scope, input);
  const { intent } = attempt;
  if (intent.quarantinedAt) {
    throw new FolderRestoreIntentError(
      "This folder restore was quarantined after a revision conflict.",
      intent
    );
  }
  try {
    const response = await api.restoreTrash("folder", intent.folderId, {
      baseRev: intent.fromRev,
      idempotencyKey: intent.idempotencyKey
    });
    return { response, intent, reused: attempt.reused };
  } catch (error) {
    throw new FolderRestoreIntentError(
      error instanceof Error ? error.message : "Could not restore folder.",
      intent,
      error
    );
  }
}

function allOutboxItems(scope: OfflineStorageScope): Promise<OfflineOutboxItem[]> {
  return withStore<OfflineOutboxItem[]>(scope, "outbox", "readonly", (store) => store.getAll()).then((items) => items || []);
}

export function readOfflineSaveConflicts(scope: OfflineStorageScope): Promise<OfflineSaveConflict[]> {
  return withStore<OfflineSaveConflict[]>(
    scope,
    "outboxConflicts",
    "readonly",
    (store) => store.getAll()
  ).then((items) => items || []);
}

export function offlineSaveRecoveryText(
  scope: OfflineStorageScope,
  conflicts: OfflineSaveConflict[]
) {
  const normalized = requireScope(scope);
  return JSON.stringify({
    format: "notey-g.offline-note-conflicts",
    version: 1,
    exportedAt: new Date().toISOString(),
    scope: normalized,
    conflicts
  }, null, 2);
}

export function isPermanentOfflineSaveConflict(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409;
}

export async function queueOfflineNoteSave(
  scope: OfflineStorageScope,
  item: Omit<OfflineOutboxItem, "id" | "updatedAt">
) {
  const existing = (await allOutboxItems(scope)).find((candidate) => candidate.noteId === item.noteId);
  const next: OfflineOutboxItem = {
    ...existing,
    ...item,
    updatedAt: new Date().toISOString()
  };
  const id = await withStore<IDBValidKey>(scope, "outbox", "readwrite", (store) => store.put(next));
  const persisted = {
    ...next,
    id: Number(id ?? next.id)
  };
  await cacheNote(scope, {
    id: item.noteId,
    title: item.title,
    body: item.body,
    tags: item.tags || [],
    folderId: item.folderId,
    updatedAt: next.updatedAt
  });
  return persisted;
}

export async function pendingOfflineSaveCount(scope: OfflineStorageScope) {
  return (await allOutboxItems(scope)).length;
}

async function deleteOutboxItem(scope: OfflineStorageScope, id: number) {
  await withStore(scope, "outbox", "readwrite", (store) => store.delete(id));
}

function conflictId(item: OfflineOutboxItem) {
  return `outbox:${item.id}:${encodeURIComponent(item.noteId)}`;
}

async function deadLetterOutboxItem(
  scope: OfflineStorageScope,
  item: OfflineOutboxItem,
  error: ApiError
) {
  if (!item.id) throw new Error("Offline recovery requires a persisted outbox item.");
  const conflict: OfflineSaveConflict = {
    id: conflictId(item),
    sourceOutboxId: item.id,
    noteId: item.noteId,
    title: item.title,
    body: item.body,
    tags: item.tags,
    folderId: item.folderId,
    updatedAt: item.updatedAt,
    failedAt: new Date().toISOString(),
    status: error.status,
    message: error.message
  };
  const db = await openOfflineDb(scope);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(["outbox", "outboxConflicts"], "readwrite");
    tx.objectStore("outboxConflicts").put(conflict);
    tx.objectStore("outbox").delete(item.id!);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Offline recovery transaction failed."));
    };
  });
  return conflict;
}

export async function quarantineOfflineNoteSave(
  scope: OfflineStorageScope,
  item: Omit<OfflineOutboxItem, "id" | "updatedAt">,
  error: ApiError
) {
  const queued = await queueOfflineNoteSave(scope, item);
  return deadLetterOutboxItem(scope, queued, error);
}

export async function flushOfflineNoteSaves(
  scope: OfflineStorageScope
): Promise<OfflineSaveFlushResult> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return {
      flushed: 0,
      pending: await pendingOfflineSaveCount(scope),
      conflicts: 0,
      failed: 0
    };
  }
  const items = await allOutboxItems(scope);
  let flushed = 0;
  let conflicts = 0;
  let failed = 0;
  let lastError = "";
  for (const item of items) {
    if (!item.id) continue;
    try {
      // Body authority is the irreversible gate. A rich-generation lock must
      // be discovered before applying otherwise-valid folder/tag metadata,
      // or one poisoned legacy edit can leave a partially moved note behind.
      const current = await api.getNote(item.noteId);
      const saved = await saveLegacyNoteAuthorityFirst({
        noteId: item.noteId,
        baseline: current.note,
        title: item.title,
        body: item.body,
        tags: item.tags ?? current.note.tags ?? [],
        folderId: item.folderId !== undefined
          ? item.folderId
          : current.note.folderId
      });
      await cacheNote(scope, saved.note);
      await deleteOutboxItem(scope, item.id);
      flushed += 1;
    } catch (error) {
      if (isPermanentOfflineSaveConflict(error)) {
        await deadLetterOutboxItem(scope, item, error);
        conflicts += 1;
        continue;
      }
      failed += 1;
      lastError = error instanceof Error
        ? error.message
        : "An offline edit could not be synced.";
    }
  }
  return {
    flushed,
    pending: await pendingOfflineSaveCount(scope),
    conflicts,
    failed,
    ...(lastError ? { lastError } : {})
  };
}

type LegacyConflict = Omit<OfflineSaveConflict, "id" | "sourceOutboxId"> & {
  outboxId?: number;
};

async function openLegacyDb() {
  if (!canUseIndexedDb()) throw new Error("Offline storage is not available in this browser.");
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(LEGACY_DB_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Could not inspect legacy offline storage."));
  });
}

async function legacyRows<T>(storeName: string): Promise<T[]> {
  const db = await openLegacyDb();
  if (!db.objectStoreNames.contains(storeName)) {
    db.close();
    return [];
  }
  return new Promise<T[]>((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const request = tx.objectStore(storeName).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error || new Error("Could not inspect legacy offline records."));
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Could not inspect legacy offline records."));
    };
  });
}

async function deleteLegacyRows(storeName: string, keys: IDBValidKey[]) {
  if (!keys.length) return;
  const db = await openLegacyDb();
  if (!db.objectStoreNames.contains(storeName)) {
    db.close();
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    for (const key of keys) store.delete(key);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Could not clean up migrated legacy offline records."));
    };
  });
}

async function saveMigratedConflict(
  scope: OfflineStorageScope,
  conflict: LegacyConflict,
  source: "conflict" | "outbox" = "conflict"
) {
  const id = `legacy:${source}:${conflict.outboxId ?? "unknown"}:${encodeURIComponent(conflict.noteId)}`;
  const migrated: OfflineSaveConflict = {
    ...conflict,
    id,
    ...(conflict.outboxId !== undefined ? { sourceOutboxId: conflict.outboxId } : {})
  };
  await withStore(scope, "outboxConflicts", "readwrite", (store) => store.put(migrated));
  await cacheNote(scope, {
    id: migrated.noteId,
    title: migrated.title,
    body: migrated.body,
    tags: migrated.tags || [],
    folderId: migrated.folderId,
    updatedAt: migrated.updatedAt
  });
}

const legacyMigrationPromises = new Map<string, Promise<{
  migrated: number;
  retained: number;
}>>();

export function migrateLegacyOfflineData(
  scope: OfflineStorageScope,
  {
    canAccessNote = async (noteId: string) => {
      try {
        await api.getNote(noteId);
        return true;
      } catch {
        return false;
      }
    }
  }: {
    canAccessNote?: (noteId: string) => Promise<boolean>;
  } = {}
) {
  const databaseName = offlineStorageDatabaseName(scope);
  const active = legacyMigrationPromises.get(databaseName);
  if (active) return active;
  const migration = (async () => {
    const [outbox, conflicts] = await Promise.all([
      legacyRows<OfflineOutboxItem>("outbox"),
      legacyRows<LegacyConflict>("outboxConflicts")
    ]);
    const byNote = new Map<string, {
      outbox: OfflineOutboxItem[];
      conflicts: LegacyConflict[];
    }>();
    for (const item of outbox) {
      const group = byNote.get(item.noteId) || { outbox: [], conflicts: [] };
      group.outbox.push(item);
      byNote.set(item.noteId, group);
    }
    for (const conflict of conflicts) {
      const group = byNote.get(conflict.noteId) || { outbox: [], conflicts: [] };
      group.conflicts.push(conflict);
      byNote.set(conflict.noteId, group);
    }

    let migrated = 0;
    let retained = 0;
    const outboxKeys: IDBValidKey[] = [];
    const conflictKeys: IDBValidKey[] = [];
    for (const [noteId, group] of byNote) {
      if (!(await canAccessNote(noteId))) {
        retained += group.outbox.length + group.conflicts.length;
        continue;
      }
      if (group.conflicts.length) {
        for (const conflict of group.conflicts) {
          await saveMigratedConflict(scope, conflict);
          if (conflict.outboxId !== undefined) conflictKeys.push(conflict.outboxId);
          migrated += 1;
        }
        // A crash or a later offline edit can leave distinct drafts in both
        // stores. Preserve every exact body as recovery evidence, but do not
        // allow any copy of an already-conflicted note to retry.
        for (const item of group.outbox) {
          await saveMigratedConflict(scope, {
            ...item,
            outboxId: item.id,
            failedAt: new Date().toISOString(),
            status: 409,
            message: "Preserved from an unscoped legacy outbox after a generation conflict."
          }, "outbox");
          if (item.id !== undefined) outboxKeys.push(item.id);
          migrated += 1;
        }
      } else {
        const latest = [...group.outbox].sort((left, right) =>
          String(left.updatedAt || "").localeCompare(String(right.updatedAt || ""))
        ).at(-1);
        if (latest) {
          await queueOfflineNoteSave(scope, {
            noteId: latest.noteId,
            title: latest.title,
            body: latest.body,
            tags: latest.tags,
            folderId: latest.folderId
          });
          migrated += 1;
        }
        for (const item of group.outbox) {
          if (item.id !== undefined) outboxKeys.push(item.id);
        }
      }
    }
    await deleteLegacyRows("outbox", outboxKeys);
    await deleteLegacyRows("outboxConflicts", conflictKeys);
    return { migrated, retained };
  })().finally(() => {
    legacyMigrationPromises.delete(databaseName);
  });
  legacyMigrationPromises.set(databaseName, migration);
  return migration;
}

export async function deleteOfflineStorage(scope: OfflineStorageScope) {
  if (!canUseIndexedDb()) return;
  const name = offlineStorageDatabaseName(scope);
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error || new Error("Could not delete scoped offline storage."));
    request.onblocked = () => reject(new Error("Scoped offline storage deletion was blocked."));
  });
}
